/**
 * TimeTrack - Dexie Database
 *
 * IndexedDB Schema für Offline-First Storage.
 * Alle Daten werden lokal gespeichert und im Hintergrund synchronisiert.
 */

import Dexie, { type Table } from 'dexie';
import type { TimeSheet, TimeEntry, Employee, TenantConfig } from '@timetrack/domain';
import type { PendingOperation, SyncMetadata } from './types';

// =============================================================================
// Database Class
// =============================================================================

export class TimeTrackDB extends Dexie {
  // Core Tables (Offline-First)
  timeSheet!: Table<TimeSheet, string>;
  timeEntry!: Table<TimeEntry, string>;

  // Stammdaten (Server-synced, read-only)
  employee!: Table<Employee, string>;
  tenantConfig!: Table<TenantConfig, string>;

  // Sync Infrastructure
  pendingOp!: Table<PendingOperation, number>;
  syncMeta!: Table<SyncMetadata, string>;

  constructor() {
    super('timetrack');

    this.version(1).stores({
      // Core Tables
      timeSheet: 'id, tenantId, date, status, foremanId, [tenantId+date], [tenantId+status]',
      timeEntry: 'id, timeSheetId, employeeId, [timeSheetId+employeeId]',

      // Stammdaten
      employee: 'id, tenantId, personnelNumber, foremanId, active, [tenantId+active]',
      tenantConfig: 'id',

      // Sync Infrastructure
      pendingOp: '++localId, entityType, entityId, timestamp',
      syncMeta: 'key',
    });
  }
}

// =============================================================================
// Singleton Instance
// =============================================================================

export const db = new TimeTrackDB();

// =============================================================================
// Database Utilities
// =============================================================================

/**
 * Löscht alle Inhalte aus allen Tabellen.
 * Schema bleibt erhalten - für normalen Reset.
 */
export async function clearDatabase(): Promise<void> {
  await db.transaction('rw', db.tables, async () => {
    for (const table of db.tables) {
      await table.clear();
    }
  });
}

/**
 * Löscht die gesamte IndexedDB und erstellt sie neu.
 * Nutzen bei Schema-Änderungen für sauberen Stand.
 */
export async function deleteDatabase(): Promise<void> {
  await db.delete();
}

/**
 * Prüft ob die Datenbank leer ist (erster Start).
 */
export async function isDatabaseEmpty(): Promise<boolean> {
  const count = await db.timeSheet.count();
  return count === 0;
}

/**
 * Gibt Statistiken zur Datenbank zurück.
 */
export async function getDatabaseStats(): Promise<DatabaseStats> {
  const [timeSheetsCount, timeEntriesCount, pendingOpsCount, employeesCount, hasTenantConfig] =
    await Promise.all([
      db.timeSheet.count(),
      db.timeEntry.count(),
      db.pendingOp.count(),
      db.employee.count(),
      db.tenantConfig.count().then((c) => c > 0),
    ]);

  return {
    timeSheets: timeSheetsCount,
    timeEntries: timeEntriesCount,
    pendingOps: pendingOpsCount,
    employees: employeesCount,
    hasTenantConfig,
  };
}

export interface DatabaseStats {
  timeSheets: number;
  timeEntries: number;
  pendingOps: number;
  employees: number;
  hasTenantConfig: boolean;
}

// =============================================================================
// Sync Metadata Helpers
// =============================================================================

/**
 * Speichert einen Sync-Metadaten-Wert.
 */
export async function setSyncMeta(key: string, value: string): Promise<void> {
  await db.syncMeta.put({ key, value });
}

/**
 * Liest einen Sync-Metadaten-Wert.
 */
export async function getSyncMeta(key: string): Promise<string | undefined> {
  const record = await db.syncMeta.get(key);
  return record?.value;
}

/**
 * Gibt den Zeitpunkt des letzten erfolgreichen Syncs zurück.
 */
export async function getLastSyncTimestamp(): Promise<string | null> {
  const value = await getSyncMeta('lastSync');
  return value ?? null;
}

/**
 * Setzt den Zeitpunkt des letzten erfolgreichen Syncs.
 */
export async function setLastSyncTimestamp(timestamp: string): Promise<void> {
  await setSyncMeta('lastSync', timestamp);
}

/**
 * Gibt die Device-ID zurück (oder generiert eine neue).
 */
export async function getDeviceId(): Promise<string> {
  let deviceId = await getSyncMeta('deviceId');

  if (!deviceId) {
    // Lazy import to avoid circular dependency
    const { createId } = await import('@paralleldrive/cuid2');
    deviceId = createId();
    await setSyncMeta('deviceId', deviceId);
  }

  return deviceId;
}

// =============================================================================
// Stammdaten Helpers (Employees & TenantConfig)
// =============================================================================

/**
 * Liest alle Employees aus der lokalen DB.
 */
export async function getCachedEmployees(): Promise<Employee[]> {
  return db.employee.toArray();
}

/**
 * Speichert Employees in der lokalen DB (ersetzt alle bestehenden).
 */
export async function setCachedEmployees(employees: Employee[]): Promise<void> {
  await db.transaction('rw', db.employee, async () => {
    await db.employee.clear();
    await db.employee.bulkAdd(employees);
  });
}

/**
 * Liest die TenantConfig aus der lokalen DB.
 */
export async function getCachedTenantConfig(): Promise<TenantConfig | null> {
  const configs = await db.tenantConfig.toArray();
  return configs[0] ?? null;
}

/**
 * Speichert die TenantConfig in der lokalen DB.
 */
export async function setCachedTenantConfig(tenantConfig: TenantConfig): Promise<void> {
  await db.transaction('rw', db.tenantConfig, async () => {
    await db.tenantConfig.clear();
    await db.tenantConfig.add(tenantConfig);
  });
}

// =============================================================================
// Development Utilities
// =============================================================================

/**
 * Setzt die Datenbank zurück (löscht alle lokalen Daten).
 * Nur für Development! Aufruf via Console: resetDatabase()
 */
export async function resetDatabase(): Promise<void> {
  console.info('[DB] Resetting database...');

  await clearDatabase();
  console.info('[DB] Database cleared');

  console.info('[DB] Database reset complete. Reloading page...');
  window.location.reload();
}

// Expose auf window für Console-Zugriff (nur Development)
if (typeof window !== 'undefined') {
  const win = window as Window & {
    resetDatabase?: typeof resetDatabase;
    dbStats?: typeof getDatabaseStats;
  };
  win.resetDatabase = resetDatabase;
  win.dbStats = getDatabaseStats;
  console.info('[DB] Dev utilities available: resetDatabase(), dbStats()');
}
